import { on } from '@ember/modifier';
import { action } from '@ember/object';

import type { PopupListItem } from 'webapp-types';

import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import BlockDebugButtonComponent from 'webapp/components/block/debug-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args as GoalsHierarchyTableRowArgs } from 'webapp/components/goals/hierarchy-table-row';
import GoalsHierarchyTableRowComponent from 'webapp/components/goals/hierarchy-table-row';
import GoalsInsightsSubgoalCompatibilityComponent from 'webapp/components/goals/insights/subgoal-compatibility';
import type { Action as GoalAction } from 'webapp/components/goals/item-base';
import GoalsProgressComponent from 'webapp/components/goals/progress';
import GroupSelectComponent from 'webapp/components/group-select';
import OptionsButtonComponent from 'webapp/components/options-button';
import OwnerSelectComponent from 'webapp/components/owner-select';

import type { Goal } from 'webapp/models-custom';

type Action = GoalAction | 'remove';
type Option = PopupListItem<Action>;

interface Args extends GoalsHierarchyTableRowArgs {
	remove: (model: Goal) => void;
}

export default class GoalsInsightsSubgoalsRowComponent extends GoalsHierarchyTableRowComponent<Args> {
	get extendedOptions(): Option[] {
		const options: Option[] = super.options;

		if (this.args.depth > 0) {
			return options;
		}

		const removeOption: Option = {
			class: 'delete',
			text: 'Remove as sub-goal',
			action: 'remove',
			icon: 'Trash',
		};

		// insert it before "delete" action
		const deleteIndex = options.findIndex((option) => option.action === 'delete');
		if (deleteIndex === -1) {
			options.push(removeOption);
		} else {
			options.splice(deleteIndex, 0, removeOption);
		}

		return options;
	}

	@action
	remove() {
		this.args.remove(this.args.model);
	}
	<template>
		<tr data-id={{@model.id}} class={{joinClasses this.classNames}}>

			<td class="c0 c1 board">

				<div class="icon-and-title-wrapper">
					<div class="icon-wrapper">

						<CommonUiIconComponent @name={{this.iconForArrow}} @size="md" />

						<span class="target" role="button" {{on "click" this.viewDetails}}>
							<CommonUiIconComponent @name="Target" @size="md" />
							{{#if this.emblemTypeName}}
								<div class="emblem emblem--{{this.emblemTypeName}}"></div>
							{{/if}}
						</span>

						<BlockDebugButtonComponent @blockId={{@model.blockId}} />

					</div>

					<div class="title-wrapper">
						<div class="flex">
							<a role="button" class="goal-title" {{on "click" this.viewDetails}}>
								{{highlightResult text=@model.title match=@model.activeFilter}}
							</a>
							<div class="labelicons">
								{{#if @model.favorite}}
									<span class="labelicon">
										<CommonUiIconComponent @name="StarFill" @size="sm" />
									</span>
								{{/if}}
							</div>
						</div>

						<div class="flex">
							{{#if this.subgoalsCount}}
								<a
									class="toggler toggler--{{if this.isExpanded 'expanded'}}"
									{{on "click" this.toggleExpanded}}
								>
									<CommonUiIconComponent
										@name="ChevronDown"
										@size="xs"
									/>{{this.subgoalsText}}
								</a>
								<span class="dot">&centerdot;</span>
							{{/if}}

							<span>{{@model.timeframeText}}</span>

							{{#if @model.group}}
								<span class="dot">&centerdot;</span>
								<GroupSelectComponent @model={{@model}} @canEdit={{false}} />
							{{/if}}
						</div>
					</div>
				</div>
			</td>

			<td class="c2" data-label="Progress: ">
				<div class="flex">
					<GoalsInsightsSubgoalCompatibilityComponent @model={{@model}} />
					<GoalsProgressComponent
						@model={{@model}}
						@goalData={{this.goalData}}
						@size="small"
					/>
				</div>
			</td>

			<td class="c5 prio-low" data-label="Owner: ">
				<OwnerSelectComponent
					@model={{@model}}
					@canEdit={{@model.canEdit}}
					@isOwnerNameHidden={{true}}
				/>
			</td>

			<td class="c6 button-ct last">
				<OptionsButtonComponent
					@options={{this.extendedOptions}}
					@onAction={{this.doOptionsAction}}
					@skipMainAction={{true}}
				/>
			</td>
		</tr>
	</template>
}
